// src/pages/HomePage.js
import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Navbar from '../components/Navbar';
import { getAgreementByAgreementID } from '../firebase/services';
import CircleIcon, { SvgIcon } from '../components/Icon';
import { useAuth } from '../hooks/useAuth';
import { FULL_ACCESS_MODE } from '../config/flags';

const StepIcon = ({ color, children }) => (
  <div style={{
    width: 38, height: 38, borderRadius: '50%', flexShrink: 0,
    background: color, display: 'flex', alignItems: 'center', justifyContent: 'center',
    color: 'white', fontSize: '1rem',
  }}>
    {children}
  </div>
);

export default function HomePage() {
  const [findID, setFindID] = useState('');
  const [finding, setFinding] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useTranslation();

  const handleFind = async () => {
    if (!findID.trim()) return;
    setFinding(true); setError('');
    try {
      const agreement = await getAgreementByAgreementID(findID.trim());
      if (!agreement) { setError(t('home.notFound')); return; }
      if (agreement.status === 'Pending') navigate(`/confirm/${agreement.agreementID}`);
      else if (agreement.status === 'Confirmed') navigate(`/agreement-confirmed/${agreement.agreementID}`);
      else navigate(`/agreement-declined/${agreement.agreementID}`);
    } catch { setError(t('home.error')); }
    finally { setFinding(false); }
  };

  return (
    <div className="page">
      <Navbar />
      <div className="container" style={{ paddingTop: 20, paddingBottom: 60 }}>

        <div style={{ textAlign: 'center', padding: '12px 0 16px' }}>
          <img
            src="/images/tutto-logo-transparent.png"
            alt="TuttoClaro"
            style={{ height: 140, width: 'auto', objectFit: 'contain', margin: '0 auto 8px', display: 'block' }}
          />
          <h1 style={{ fontSize: '1.6rem', fontWeight: 800, lineHeight: 1.2, marginBottom: 6 }}>
            {t('home.headline')}
          </h1>
          <p style={{ color: 'var(--muted)', fontSize: '0.88rem', lineHeight: 1.5, marginBottom: 16 }}>
            {t('home.subtitle')}
          </p>
          <Link to="/create" className="btn btn-primary" style={{ maxWidth: 300, margin: '0 auto 8px' }}>
            {t('home.createAgreement')}
          </Link>
          {!user && (
            <Link to="/signup" className="btn btn-secondary" style={{ maxWidth: 300, margin: '0 auto' }}>
              {t('home.createAccount')}
            </Link>
          )}
        </div>

        <hr className="divider" />

        <p style={{ textAlign: 'center', fontSize: '0.82rem', color: 'var(--muted)', marginBottom: 10 }}>
          {t('home.orFind')}
        </p>
        <div className="find-box">
          <input
            className="form-input"
            placeholder={t('home.enterID')}
            value={findID}
            onChange={e => setFindID(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleFind()}
          />
          <button className="find-btn" onClick={handleFind} disabled={finding}>
            {finding ? '...' : t('home.find')}
          </button>
        </div>
        {error && <p className="error-msg">{error}</p>}

        <hr className="divider" />

        <p style={{ fontFamily: 'Noto Sans, sans-serif', fontWeight: 700, fontSize: '0.95rem', marginBottom: 12, textAlign: 'center' }}>
          {t('home.howItWorks')}
        </p>
        {[
          { color: '#1F4B3F', icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M12 20h9"/><path d="M16.5 3.5a2.121 2.121 0 013 3L7 19l-4 1 1-4L16.5 3.5z"/></svg>, title: t('home.step1Title'), desc: t('home.step1Desc') },
          { color: '#A7C5B6', icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M10 13a5 5 0 007.54.54l3-3a5 5 0 00-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 00-7.54-.54l-3 3a5 5 0 007.07 7.07l1.71-1.71"/></svg>, title: t('home.step2Title'), desc: t('home.step2Desc') },
          { color: '#D97A5D', icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>, title: t('home.step3Title'), desc: t('home.step3Desc') },
        ].map((s, i) => (
          <div key={i} className="card" style={{ display: 'flex', gap: 12, alignItems: 'center', padding: '13px 16px', marginBottom: 8 }}>
            <StepIcon color={s.color}>{s.icon}</StepIcon>
            <div>
              <div style={{ fontWeight: 600, fontSize: '0.86rem', marginBottom: 2 }}>{s.title}</div>
              <div style={{ fontSize: '0.76rem', color: 'var(--muted)', lineHeight: 1.4 }}>{s.desc}</div>
            </div>
          </div>
        ))}

        {!FULL_ACCESS_MODE && (
          <div className="upsell-banner" style={{ marginTop: 20 }}>
            <div className="upsell-banner-title"><SvgIcon name="heart" size={14} color="var(--premium)" /> {t('home.premiumBanner')}</div>
            <Link to="/pricing" className="btn btn-premium" style={{ padding: '9px', fontSize: '0.8rem', marginBottom: 0 }}>
              {t('home.seePricing')}
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
